import { useEffect, useState } from 'react';
import { useAuth } from '../../../../../authContext';
import { useSecurity } from '../../../../../SecurityContext';
import { fetchMemberProfileCard } from '../../../../../components/tools/memberProfileCardApi';
import { pickPrimaryHandleIdFromHandleList } from '../dashboard/dashboardUtils';
import {
  getPersonalHubSessionBoot,
  personalHubSessionCacheKey,
  PERSONAL_HUB_SECTION_DASHBOARD,
} from '../tools/personalHubSessionCache';
import { personalHubViewerCacheKey } from '../tools/personalHubViewerCacheKey';

function resolveHandleId({ dashboardCacheKey, handles, isViewAsActive }) {
  const cached = getPersonalHubSessionBoot(dashboardCacheKey);
  if (cached?.dashboardHandleId) return cached.dashboardHandleId;
  if (!isViewAsActive) return pickPrimaryHandleIdFromHandleList(handles);
  return null;
}

export default function usePersonalHubHandleId() {
  const { handles, isLoggedIn, userId } = useAuth();
  const { isViewAsActive, viewAsUserId } = useSecurity();

  const viewerKey = personalHubViewerCacheKey({ userId, isViewAsActive, viewAsUserId });
  const dashboardCacheKey = personalHubSessionCacheKey(PERSONAL_HUB_SECTION_DASHBOARD, viewerKey);

  const [handleId, setHandleId] = useState(() =>
    resolveHandleId({ dashboardCacheKey, handles, isViewAsActive })
  );
  const [loading, setLoading] = useState(() => {
    if (!isLoggedIn) return false;
    return isViewAsActive && !resolveHandleId({ dashboardCacheKey, handles, isViewAsActive });
  });

  useEffect(() => {
    if (!isLoggedIn) {
      setHandleId(null);
      setLoading(false);
      return undefined;
    }

    const cachedId = getPersonalHubSessionBoot(dashboardCacheKey)?.dashboardHandleId;
    if (cachedId) {
      setHandleId(cachedId);
      setLoading(false);
      return undefined;
    }

    if (!isViewAsActive) {
      setHandleId(pickPrimaryHandleIdFromHandleList(handles));
      setLoading(false);
      return undefined;
    }

    if (viewAsUserId == null) {
      setHandleId(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const cardRes = await fetchMemberProfileCard(viewAsUserId);
        if (cancelled) return;
        const id = cardRes?.ok
          ? pickPrimaryHandleIdFromHandleList(cardRes.card?.handles)
          : null;
        setHandleId(id);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dashboardCacheKey, handles, isLoggedIn, isViewAsActive, viewAsUserId]);

  return { handleId, loading };
}
